/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/ContributorDashboard.tsx.
 */

import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardHeader from "../../../components/Layout/DashboardHeader";
import DashboardFooter from "../../../components/Layout/DashboardFooter";
import SidebarContributor from "../../../components/Layout/SidebarContributor";
import ContributorHome from "./Home/ContributorHome";
import ContributorProfile from "./Profile/ContributorProfile";
import IncompleteSubmissions from "./IncompleteSubmissions/IncompleteSubmissions";
import FlaggedSubmissions from "./FlaggedSubmissions/FlaggedSubmissions";
import DataInput from "./DataInput/DataInput";
import ContributorDetails from "./Stages/ContributorDetails/ContributorDetails";
import "./Contributor.css";
import PublicationData from "./Stages/PublicationData/PublicationData";
import BackgroundData from "./Stages/BackgroundData/BackgroundData";
import FormA1 from "./Stages/A1/FormA1";
import FormA2 from "./Stages/A2/FormA2";
import FormA3 from "./Stages/A3/FormA3";
import A1A3 from "./Stages/A1-A3/A1A3";
import Dataset from "./Stages/Dataset/Dataset";
import SubmittedDataset from "./SubmittedDataset/SubmittedDataset";
import ConfirmModal from "../../../components/common/ConfirmModal";
import BulkImportPreviewScreen from "./BulkImportPreview/BulkImportPreview";
import type { BulkPreview } from "./DataInput/components/BulkImportModal";
import {
  useEnabledStages,
  type StageName,
  buildContributorStageListFromSubmission,
} from "./hooks/useEnabledStages";
import { mapApiCurrentStageToStageName } from "./utils/mapApiStageToContributorSection";
import { getSubmissionById } from "../../../services/submissionService";
import {
  CONTRIBUTOR_SUBMITTED_DATASETS_SECTION,
  navigateToContributorSubmittedDatasets,
  resolveContributorDashboardSection,
} from "./contributorDashboardNavigation";

const ContributorDashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(
    () => resolveContributorDashboardSection(location.search, location.state) ?? "Home"
  );
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSection, setPendingSection] = useState<string | null>(null);
  const [bulkPreview, setBulkPreview] = useState<BulkPreview | null>(null);
  const activeSectionRef = useRef(activeSection);
  activeSectionRef.current = activeSection;

  useEffect(() => {
    const section = resolveContributorDashboardSection(
      location.search,
      location.state
    );
    if (section) {
      setActiveSection(section);
    }
  }, [location.pathname, location.search, location.key]);

  const clearDashboardLocationParams = () => {
    if (location.search || location.state) {
      navigate("/dashboard", { replace: true, state: null });
    }
  };

  const navigateToSubmittedDatasets = () => {
    navigateToContributorSubmittedDatasets(navigate);
  };

  const { enabledStages, refetch, syncEnabledStages } =
    useEnabledStages(submissionId);

  /** Sidebar progress uses stages without "Submitted Dataset"; indices must match that list. */
  const progressStages = (list: StageName[]) =>
    list.filter((s) => s !== "Submitted Dataset");

  const handleResumeSubmission = async (
    id: string,
    options?: { mode: "beginning" | "current"; currentStage?: string }
  ) => {
    setSubmissionId(id);
    const mode = options?.mode ?? "beginning";
    if (mode === "beginning") {
      setActiveSection("Contributor's Details");
      setCurrentStageIndex(0);
      return;
    }

    const applyFallback = (
      submission: Parameters<typeof buildContributorStageListFromSubmission>[0]
    ) => {
      syncEnabledStages(
        buildContributorStageListFromSubmission(
          submission,
          options?.currentStage
        )
      );
      setActiveSection("Contributor's Details");
      setCurrentStageIndex(0);
    };

    try {
      const res = await getSubmissionById(id);
      const root = res?.data;
      const submission =
        root?.success && root?.data ? root.data : null;

      const apiStage =
        options?.currentStage ??
        submission?.currentStage ??
        submission?.current_stage;
      const target = mapApiCurrentStageToStageName(apiStage);

      if (!target || target === "Submitted Dataset") {
        applyFallback(submission);
        return;
      }

      const stageList = buildContributorStageListFromSubmission(
        submission,
        options?.currentStage
      );
      const idx = progressStages(stageList).indexOf(target);
      if (idx < 0) {
        applyFallback(submission);
        return;
      }

      syncEnabledStages(stageList);
      setActiveSection(target);
      setCurrentStageIndex(idx);
    } catch {
      applyFallback(null);
    }
  };
  const stages: StageName[] =
    enabledStages?.length > 0
      ? enabledStages
      : ["Contributor's Details", "Publication Details", "Background Data"];

  // ✅ Advances to the next enabled stage in sidebar order (skips unselected LCA stages).
  const handleNextStage = (stagesOverride?: StageName[]) => {
    const list = stagesOverride ?? stages;
    const progress = progressStages(list);
    if (!progress.length) return;

    const current = activeSectionRef.current as StageName;
    const fromIdx = progress.indexOf(current);
    const nextIdx =
      fromIdx >= 0
        ? Math.min(fromIdx + 1, progress.length - 1)
        : Math.min(currentStageIndex + 1, progress.length - 1);
    const nextSection = progress[nextIdx];
    if (nextSection) {
      setActiveSection(nextSection);
      setCurrentStageIndex(nextIdx);
    }
  };

  const moveToSubmittedDatSet = () => {
    // After final submission, clear the active submission and reset stages
    setSubmissionId(null);
    setCurrentStageIndex(0);
    setActiveSection(CONTRIBUTOR_SUBMITTED_DATASETS_SECTION);
    navigateToSubmittedDatasets();
  };

  // Check if a stage is currently active
  const isStageActive = () => {
    return submissionId !== null && stages.includes(activeSection as StageName);
  };

  // Handle sidebar selection with confirmation
  const handleSidebarSelect = (section: string) => {
    // If a stage is active and user is trying to navigate to a non-stage section
    if (isStageActive() && !stages.includes(section as StageName)) {
      setPendingSection(section);
      setShowConfirmModal(true);
      return;
    }

    // Prevent accessing stages without submissionId
    if (stages.includes(section as StageName) && !submissionId) {
      setActiveSection("Data Input");
      return;
    }

    // Normal navigation
    setActiveSection(section);
    if (section === CONTRIBUTOR_SUBMITTED_DATASETS_SECTION) {
      navigateToSubmittedDatasets();
    } else {
      clearDashboardLocationParams();
    }

    // Keep sidebar progress in sync (indices match filtered stage list)
    const idx = progressStages(stages).indexOf(section as StageName);
    if (idx >= 0) setCurrentStageIndex(idx);
  };

  // Handle confirmation to navigate away
  const handleConfirmNavigation = () => {
    if (pendingSection) {
      setSubmissionId(null); // Clear submission ID
      setCurrentStageIndex(0); // Reset stage index
      setActiveSection(pendingSection);
      setPendingSection(null);
      if (pendingSection === CONTRIBUTOR_SUBMITTED_DATASETS_SECTION) {
        navigateToSubmittedDatasets();
      } else {
        clearDashboardLocationParams();
      }
    }
    setShowConfirmModal(false);
  };

  // Handle cancel navigation
  const handleCancelNavigation = () => {
    setShowConfirmModal(false);
    setPendingSection(null);
  };

  const renderContent = () => {
    switch (activeSection) {
      case "Home":
        return <ContributorHome onNavigateToDataInput={() => handleSidebarSelect("Data Input")} />;
      case "Profile":
        return <ContributorProfile />;
      case "IncompleteSubmissions":
        return (
          <IncompleteSubmissions onResumeSubmission={handleResumeSubmission} />
        );
      case "FlaggedSubmissions":
        return (
          <FlaggedSubmissions onResumeSubmission={handleResumeSubmission} />
        );
      case "Data Input":
        return (
          <DataInput
            onNext={(newId) => {
              setSubmissionId(newId);
              setActiveSection("Contributor's Details");
              setCurrentStageIndex(0);
            }}
            onBulkParsed={(preview) => {
              setBulkPreview(preview);
              setActiveSection("Bulk Import Preview");
            }}
          />
        );
      case "Bulk Import Preview":
        return (
          <BulkImportPreviewScreen
            preview={bulkPreview}
            onBack={() => {
              setBulkPreview(null);
              setActiveSection("Data Input");
            }}
          />
        );
      case "Contributor's Details":
        return (
          <ContributorDetails
            onNextStage={handleNextStage}
            submissionId={submissionId!}
          />
        );
      case "Publication Details":
        return (
          <PublicationData
            onNextStage={handleNextStage}
            submissionId={submissionId!}
          />
        );
      case "Background Data":
        return (
          <BackgroundData
            onNextStage={async () => {
              const freshStages = await refetch();
              handleNextStage(freshStages);
            }}
            submissionId={submissionId!}
          />
        );
      case "A1":
        return (
          <FormA1 onNextStage={handleNextStage} submissionId={submissionId!} />
        );
      case "A2":
        return (
          <FormA2 onNextStage={handleNextStage} submissionId={submissionId!} />
        );
      case "A3":
        return (
          <FormA3 onNextStage={handleNextStage} submissionId={submissionId!} />
        );
      case "A1-A3":
        return (
          <A1A3 onNextStage={handleNextStage} submissionId={submissionId!} />
        );
      case "Dataset":
        return (
          <Dataset
            onNextStage={moveToSubmittedDatSet}
            submissionId={submissionId!}
          />
        );
      case "SubmittedDataset":
        return <SubmittedDataset />;
      default:
        return (
          <div className="contributor-section">
            <h2>{activeSection}</h2>
            <p>Section under construction...</p>
          </div>
        );
    }
  };

  return (
    <div className="dashboard-container">
      <DashboardHeader />

      <div className="dashboard-body">
        <SidebarContributor
          onSelect={handleSidebarSelect}
          currentStage={currentStageIndex}
          submissionId={submissionId}
          activeSection={activeSection}
          stages={stages.filter((s) => s !== "Submitted Dataset")}
        />

        <main className="dashboard-content">
          <div className="content-wrapper">{renderContent()}</div>
        </main>
      </div>

      <DashboardFooter />

      {/* Confirmation Modal */}
      <ConfirmModal
        open={showConfirmModal}
        title="Leave Current Submission?"
        description="You are currently working on a submission. If you navigate away, your current progress will be saved, but you'll need to resume the submission to continue. Do you want to continue?"
        onCancel={handleCancelNavigation}
        onConfirm={handleConfirmNavigation}
        confirmText="Yes, Continue"
        cancelText="Cancel"
      />
    </div>
  );
};

/** React component — Contributor Dashboard. */
export default ContributorDashboard;
